import toast from 'react-hot-toast';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import ShowImageSelector from './ShowImageSelector';
import styles from './ShowModal.module.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShowModal = ({ mode = 'add', show = null, onClose, onSuccess, user = null }) => {
  const isEditMode = mode === 'edit';
  
  const [formData, setFormData] = useState({
    title: isEditMode ? show?.title || '' : '',
    image_url: isEditMode ? show?.image_url || '' : '',
    backdrop_url: isEditMode ? show?.backdrop_url || '' : '',
    release_date: isEditMode ? show?.release_date || '' : '',
    genres: isEditMode ? show?.genres || [] : [],
    overview: isEditMode ? show?.overview || '' : '',
    status: isEditMode ? show?.status || 'Currently Watching' : 'Currently Watching',
    streaming_service: isEditMode ? show?.streaming_service || '' : '',
    custom_service: isEditMode ? show?.custom_service || '' : '',
    personal_rating: isEditMode ? show?.personal_rating || '' : '',
    personal_review: isEditMode ? show?.personal_review || '' : '',
    tmdb_id: isEditMode ? show?.tmdb_id || null : null,
    media_type: isEditMode ? show?.media_type || null : null
  });
  
  const [services, setServices] = useState([]);
  const [showCustomService, setShowCustomService] = useState(
    isEditMode ? !!show?.custom_service : false
  );
  const [shareToFeed, setShareToFeed] = useState(!isEditMode);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingShows, setSearchingShows] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    loadServices();
  }, [user?.region]);

  const loadServices = async () => {
    try {
      const url = user?.region
        ? `${API}/streaming-services?region=${encodeURIComponent(user.region)}`
        : `${API}/streaming-services`;
      const response = await axios.get(url, { withCredentials: true });
      setServices(response.data);
    } catch (error) {
      console.error('Error loading services:', error);
    }
  };

  const searchShows = async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setSearchingShows(true);
    try {
      const response = await axios.get(`${API}/search-tmdb`, {
        params: { q: query },
        withCredentials: true
      });

      if (response.data && response.data.results) {
        const shows = response.data.results.slice(0, 8).map(show => ({
          tmdb_id: show.tmdb_id,
          title: show.title,
          year: show.release_date ? show.release_date.substring(0, 4) : 'N/A',
          poster: show.poster_path ? `https://image.tmdb.org/t/p/w200${show.poster_path}` : null,
          fullPoster: show.poster_path ? `https://image.tmdb.org/t/p/w500${show.poster_path}` : null,
          backdrop: show.backdrop_path ? `https://image.tmdb.org/t/p/w780${show.backdrop_path}` : null,
          release_date: show.release_date || '',
          genres: show.genres || [],
          overview: show.overview || '',
          media_type: show.media_type || 'tv',
          origin_country: show.origin_country || null
        }));
        setSuggestions(shows);
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error('Error searching shows:', error);
      setSuggestions([]);
    } finally {
      setSearchingShows(false);
    }
  };

  const handleTitleChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, title: value });
    if (!isEditMode) {
      searchShows(value);
    }
  };

  const selectShow = (show) => {
    setFormData({
      ...formData,
      title: show.title,
      image_url: show.fullPoster || '',
      backdrop_url: show.backdrop || '',
      release_date: show.release_date || '',
      genres: show.genres || [],
      overview: show.overview || '',
      tmdb_id: show.tmdb_id || null,
      media_type: show.media_type || 'tv'
    });
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        title: formData.title,
        image_url: formData.image_url || null,
        backdrop_url: formData.backdrop_url || null,
        release_date: formData.release_date || null,
        genres: formData.genres && formData.genres.length > 0 ? formData.genres : null,
        overview: formData.overview || null,
        status: formData.status,
        streaming_service: showCustomService ? null : formData.streaming_service,
        custom_service: showCustomService ? formData.custom_service : null,
        personal_rating: formData.personal_rating ? parseInt(formData.personal_rating) : null,
        personal_review: formData.personal_review || null,
        tmdb_id: formData.tmdb_id || null,
        media_type: formData.media_type || null
      };

      let response;
      if (isEditMode) {
        // Update existing show
        response = await axios.put(`${API}/shows/${show.id}`, payload, { withCredentials: true });
      } else {
        // Create new show
        response = await axios.post(`${API}/shows`, payload, { withCredentials: true });
      }
      
      const savedShow = response.data;

      // If user has rating and review and wants to share, post to feed
      if (shareToFeed && formData.personal_rating && formData.personal_review) {
        try {
          await axios.post(
            `${API}/reviews`,
            {
              show_id: savedShow.id,
              show_title: formData.title,
              rating: parseInt(formData.personal_rating),
              review_text: formData.personal_review
            },
            { withCredentials: true }
          );
        } catch (reviewError) {
          console.error('Error sharing review:', reviewError);
        }
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'adding'} show:`, error);
      
      // Handle duplicate show error
      if (error.response?.status === 409) {
        const errorData = error.response.data.detail;
        const showTitle = errorData.existing_show?.title || formData.title;
        const showId = errorData.show_id;
        
        toast.error(
          <div>
            <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
              {errorData.message || `You've already added "${showTitle}"`}
            </div>
            <div style={{ fontSize: '0.9rem' }}>
              Go to My Shows to edit or update this entry.
            </div>
          </div>,
          { duration: 5000 }
        );
        
        // Close modal after showing duplicate message
        onClose();
      } else {
        toast.error(`Error ${isEditMode ? 'updating' : 'adding'} show. Please try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Show delete confirmation dialog
  const handleDeleteClick = () => {
    console.log('Delete Show button clicked in modal for show:', show?.id);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    console.log('User confirmed delete in modal, making API call...');
    setShowDeleteConfirm(false);
    setLoading(true);
    
    try {
      const response = await axios.delete(`${API}/shows/${show.id}`, { withCredentials: true });
      console.log('Delete API response from modal:', response);
      
      toast.success('Show deleted successfully');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error deleting show:', error);
      
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.');
      } else if (error.response?.status === 404) {
        toast.error('Show not found. It may have already been deleted.');
      } else {
        toast.error('Error deleting show. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className={styles.showModal}>
        <DialogHeader className={styles.modalHeader}>
          <DialogTitle>{isEditMode ? 'Edit Show' : 'Add New Show'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className={styles.modalForm}>
          <div className={styles.modalScrollContent}>
            <div className={styles.formGroup}>
              <Label htmlFor="title">Show Title *</Label>
              {!isEditMode ? (
                <div className={styles.titleInputWrapper}>
                  <Input
                    id="title"
                    data-testid="input-title"
                    value={formData.title}
                    onChange={handleTitleChange}
                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    placeholder="Start typing to search..."
                    required
                  />
                  {searchingShows && <div className={styles.searchingIndicator}>Searching...</div>}
                  {showSuggestions && suggestions.length > 0 && (
                    <div className={styles.suggestionsDropdown} data-testid="suggestions-dropdown">
                      {suggestions.map((show) => (
                        <div
                          key={show.tmdb_id || show.title}
                          className={styles.suggestionItem}
                          onClick={() => selectShow(show)}
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            selectShow(show);
                          }}
                          data-testid={`suggestion-${show.tmdb_id}`}
                        >
                          {show.poster && (
                            <img src={show.poster} alt={show.title} className={styles.suggestionPoster} />
                          )}
                          <div className={styles.suggestionInfo}>
                            <div className={styles.suggestionName}>{show.title}</div>
                            <div className={styles.suggestionMeta}>
                              <span className={styles.suggestionYear}>{show.year}</span>
                              {show.origin_country && (
                                <span className={styles.suggestionCountry}> ({show.origin_country})</span>
                              )}
                              {show.genres && show.genres.length > 0 && (
                                <span className={styles.suggestionGenres}> • {show.genres.slice(0, 2).join(', ')}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Input
                  id="title"
                  data-testid="edit-input-title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              )}
            </div>

            <ShowImageSelector
              showTitle={formData.title}
              onImageSelected={(imageUrl) => setFormData({ ...formData, image_url: imageUrl })}
              onBackdropSelected={(backdropUrl) => setFormData({ ...formData, backdrop_url: backdropUrl })}
              onGenresSelected={(genres) => setFormData({ ...formData, genres: genres })}
              onReleaseDateSelected={(releaseDate) => setFormData({ ...formData, release_date: releaseDate })}
              onTmdbDataSelected={(tmdbId, mediaType) => setFormData({ ...formData, tmdb_id: tmdbId, media_type: mediaType })}
              currentImageUrl={formData.image_url}
              currentBackdropUrl={formData.backdrop_url}
            />

            {(formData.release_date || (formData.genres && formData.genres.length > 0)) && (
              <div className={styles.showMetadata}>
                {formData.release_date && (
                  <div className={styles.metadataItem}>
                    <span className={styles.metadataLabel}>Released:</span>
                    <span className={styles.metadataValue}>{formData.release_date.substring(0, 4)}</span>
                  </div>
                )}
                {formData.genres && formData.genres.length > 0 && (
                  <div className="metadata-item genres-list">
                    {formData.genres.slice(0, 3).map((genre, index) => (
                      <span key={index} className={styles.genreBadge}>{genre}</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className={styles.formGroup}>
              <Label htmlFor="status">Status *</Label>
              <div className={styles.statusButtonsContainer}>
                {['Currently Watching', 'Waiting for Next Season', 'Completed', 'Want to Watch'].map((status) => (
                  <button
                    key={status}
                    type="button"
                    className={`${styles.statusButton} ${formData.status === status ? styles.statusButtonActive : ''}`}
                    onClick={() => setFormData({ ...formData, status })}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.formGroup}>
              <Label>Streaming Service</Label>
              <div className={styles.serviceToggle}>
                <Button
                  type="button"
                  variant={!showCustomService ? "default" : "outline"}
                  onClick={() => setShowCustomService(false)}
                  className={styles.toggleBtn}
                >
                  Select a Service
                </Button>
                <Button
                  type="button"
                  variant={showCustomService ? "default" : "outline"}
                  onClick={() => setShowCustomService(true)}
                  className={styles.toggleBtn}
                >
                  Add Your Own Service
                </Button>
              </div>

              {!showCustomService ? (
                <Select
                  value={formData.streaming_service}
                  onValueChange={(value) => setFormData({ ...formData, streaming_service: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((service) => (
                      <SelectItem key={service.id} value={service.name}>
                        {service.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  placeholder="Enter custom service name"
                  value={formData.custom_service}
                  onChange={(e) => setFormData({ ...formData, custom_service: e.target.value })}
                />
              )}
            </div>

            <div className={styles.formGroup}>
              <Label htmlFor="rating">Your Rating (1-10)</Label>
              <Input
                id="rating"
                type="number"
                min="1"
                max="10"
                value={formData.personal_rating}
                onChange={(e) => setFormData({ ...formData, personal_rating: e.target.value })}
                placeholder="Rate this show"
              />
            </div>

            <div className={styles.formGroup}>
              <Label htmlFor="review">Your Review</Label>
              <Textarea
                id="review"
                value={formData.personal_review}
                onChange={(e) => setFormData({ ...formData, personal_review: e.target.value })}
                placeholder="Write your thoughts about this show..."
                rows={4}
              />
            </div>

            {formData.personal_rating && formData.personal_review && (
              <div className={styles.shareToggleSection}>
                <div className={styles.shareToggleDescription}>
                  {isEditMode ? (
                    <p>If you would like this review to share to your feed when saved, please select that option below.</p>
                  ) : (
                    <p>This review will share to your feed when saved. If you prefer to keep it private, please select that option below.</p>
                  )}
                </div>
                <div className={styles.shareToggleButtons}>
                  <Button
                    type="button"
                    variant={shareToFeed ? "default" : "outline"}
                    onClick={() => setShareToFeed(true)}
                    className={styles.toggleBtn}
                  >
                    📢 Share to Feed Now
                  </Button>
                  <Button
                    type="button"
                    variant={!shareToFeed ? "default" : "outline"}
                    onClick={() => setShareToFeed(false)}
                    className={styles.toggleBtn}
                  >
                    🔒 Keep Private
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className={styles.formActions}>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            {isEditMode && (
              <Button type="button" variant="destructive" onClick={handleDeleteClick} disabled={loading}>
                Delete Show
              </Button>
            )}
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : isEditMode ? 'Save Changes' : 'Add Show'}
            </Button>
          </div>
        </form>
      </DialogContent>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className={styles.deleteConfirmDialog}>
          <DialogHeader>
            <DialogTitle>Delete Show</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{show?.title}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default ShowModal;
